import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FileDown } from 'lucide-react';
import PrintPreview from '../../components/printPreview';

function PreviewSection({
  selectedProblems,
  problemDetails,
  mapData,
  stamp,
  onProblemClick,
  setPrintFunction,
  lastAddedProblemId,
  editingProblemId,
  isLoadingMore
}) {
  // 선택된 문제 정보를 내부 상태로 관리하여 유지 (문제 내용 포함)
  const [localProblems, setLocalProblems] = useState([]);
  // 문제 내용을 별도로 캐싱하는 상태
  const [cachedContents, setCachedContents] = useState({});
  // 미리보기 확대/축소 비율 상태 추가
  const [scale, setScale] = useState(0.7);
  // PDF 생성 중 상태
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [showPdfInput, setShowPdfInput] = useState(false);
  const [pdfFilename, setPdfFilename] = useState('');
  // 컨테이너 ref
  const containerRef = useRef(null);
  // PrintPreview ref (PDF용 HTML 추출)
  const printPreviewRef = useRef(null);

  // 문제 상세 정보가 변경될 때마다 캐시 업데이트
  useEffect(() => {
    if (problemDetails) {
      setCachedContents(prev => ({
        ...prev,
        ...problemDetails
      }));
    }
  }, [problemDetails]);

  // 선택된 문제가 변경될 때마다 내부 상태 업데이트
  useEffect(() => {
    const prevLength = localProblems.length;
    const prevProblems = [...localProblems];
    setLocalProblems(selectedProblems);
    
    // 문제가 추가되었을 때 자동 스크롤
    if (selectedProblems.length > prevLength && lastAddedProblemId) {
      // 새로 추가된 문제가 실제로 있는지 확인
      const newProblem = selectedProblems.find(p =>
        p.problem_id === lastAddedProblemId &&
        !prevProblems.some(pp => pp.problem_id === p.problem_id)
      );

      if (newProblem) {
        setTimeout(() => {
          scrollToNewProblem(newProblem, selectedProblems.length - 1);
        }, 500); // 렌더링 완료를 위해 시간 증가

        // 첫 번째 시도가 실패할 수 있으므로 재시도 (더 긴 지연으로)
        setTimeout(() => {
          scrollToNewProblem(newProblem, selectedProblems.length - 1);
        }, 1500);
      }
    } else if (lastAddedProblemId && selectedProblems.length === prevLength) {
      // 기존 문제 포커싱 (출제 시험만 보기에서 클릭 시)
      const targetProblem = selectedProblems.find(p => p.problem_id === lastAddedProblemId);
      if (targetProblem) {
        const targetIndex = selectedProblems.findIndex(p => p.problem_id === lastAddedProblemId);
        setTimeout(() => {
          scrollToNewProblem(targetProblem, targetIndex);
        }, 300);
      }
    }
  }, [selectedProblems, lastAddedProblemId]);

  // 새로 추가된 문제로 스크롤하는 함수
  const scrollToNewProblem = (newProblem, problemIndex) => {
    // 모달 내에서 스크롤 가능한 컨테이너 찾기
    const container = containerRef.current?.closest('.overflow-auto') || 
                     containerRef.current?.parentElement?.closest('.overflow-auto') ||
                     containerRef.current?.parentElement;
    
    if (container) {
      console.log('🎯 Auto scroll target:', {
        problemId: newProblem.problem_id,
        problemIndex,
        targetNumber: problemIndex + 1
      });

      // 좌우 컬럼별로 문제 요소 수집
      const leftColumnElements = container.querySelectorAll('.problems-column:first-child .problem-item');
      const rightColumnElements = container.querySelectorAll('.problems-column:last-child .problem-item');
      
      console.log('📊 Columns info:', {
        leftCount: leftColumnElements.length,
        rightCount: rightColumnElements.length
      });
      
      let targetElement = null;
      
      // 1단계: 문제 ID로 직접 찾기
      targetElement = container.querySelector(`[data-problem-id="${newProblem.problem_id}"]`);
      
      if (targetElement) {
        console.log('✅ Found by problem ID');
      } else {
        // 2단계: 좌측 컬럼에서 문제 번호로 찾기
        const problemNumber = problemIndex + 1;
        
        for (let el of leftColumnElements) {
          if (parseInt(el.getAttribute('data-problem-number')) === problemNumber) {
            targetElement = el;
            console.log('✅ Found in left column by number');
            break;
          }
        }
        
        // 3단계: 우측 컬럼에서 문제 번호로 찾기
        if (!targetElement) {
          for (let el of rightColumnElements) {
            if (parseInt(el.getAttribute('data-problem-number')) === problemNumber) {
              targetElement = el;
              console.log('✅ Found in right column by number');
              break;
            }
          }
        }
        
        // 4단계: 인덱스 기반 찾기 (좌측 우선)
        if (!targetElement) {
          const allElements = [...Array.from(leftColumnElements), ...Array.from(rightColumnElements)];
          if (allElements.length > problemIndex) {
            targetElement = allElements[problemIndex];
            console.log('✅ Found by index fallback');
          }
        }
      }

      // 스크롤 실행
      if (targetElement) {
        try {
          // 브라우저 네이티브 scrollIntoView 사용 (가장 안정적)
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
          
          console.log('🚀 Scroll executed successfully');
        } catch (error) {
          console.error('❌ Scroll failed:', error);
        }
      } else {
        console.error('❌ Target element not found');
      }
    } else {
      console.warn('❌ Container not found');
    }
  };

  // useMemo를 사용하여 problems 배열의 참조 안정성 보장
  const problems = useMemo(() => {
    return localProblems.map(p => ({
      ...p,
      content: cachedContents[p.problem_id]?.content || problemDetails[p.problem_id]?.content || '로딩 중...'
    }));
  }, [localProblems, cachedContents, problemDetails]);

  // 확대/축소 조절 함수
  const handleScaleChange = (newScale) => {
    // 1%~100% (0.01~1.0) 범위로 제한
    if (newScale >= 0.01 && newScale <= 1.0) {
      setScale(newScale);
    }
  };

  // 입력 필드 변경 처리
  const handleInputChange = (e) => {
    const value = parseFloat(e.target.value) / 100;
    if (!isNaN(value) && value >= 0.01 && value <= 1.0) {
      setScale(value);
    }
  };

  // PDF 다운로드 처리 함수
  const handleDownloadPdf = async () => {
    if (isGeneratingPdf || localProblems.length === 0) return;

    setIsGeneratingPdf(true);

    try {
      // PrintPreview 컴포넌트에서 HTML 추출
      const previewElement = containerRef.current?.querySelector('.print-preview-container');
      if (!previewElement) {
        throw new Error('미리보기 요소를 찾을 수 없습니다.');
      }

      // HTML 클론하여 스케일 제거
      const clonedElement = previewElement.cloneNode(true);
      clonedElement.style.transform = 'none';
      clonedElement.style.width = '210mm';

      const html = clonedElement.outerHTML;

      // 현재 페이지의 스타일시트에서 필요한 스타일 추출
      const styles = Array.from(document.styleSheets)
        .map(sheet => {
          try {
            return Array.from(sheet.cssRules)
              .map(rule => rule.cssText)
              .join('\n');
          } catch (e) {
            // 외부 스타일시트는 CORS로 접근 불가할 수 있음
            return '';
          }
        })
        .join('\n');

      // Lambda API 호출
      const apiUrl = process.env.REACT_APP_PDF_API_URL;
      if (!apiUrl) {
        throw new Error('PDF API URL이 설정되지 않았습니다.');
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          html,
          styles,
          filename: (pdfFilename.trim() || `프리셋_미리보기_${new Date().toISOString().slice(0, 10)}`) + '.pdf',
        }),
      });

      if (!response.ok) {
        throw new Error(`PDF 생성 실패: ${response.status}`);
      }

      const result = await response.json();

      if (result.status !== 'success') {
        throw new Error(result.message || 'PDF 생성 중 오류가 발생했습니다.');
      }

      // Base64 PDF를 Blob으로 변환하여 다운로드
      const byteCharacters = atob(result.pdf);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      // 다운로드 트리거
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('PDF 다운로드 오류:', error);
      alert(`PDF 다운로드 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div ref={containerRef} className="relative size-full">
      {localProblems.length > 0 ? (
        <div className="relative">
          <PrintPreview 
            problems={problems}
            mode="preview"
            scale={scale}
            onProblemClick={onProblemClick}
            tree_name={mapData?.field_info?.tree_name || ''}
            edu_ver={stamp?.edu_ver || ''}
            student_name=''
            exam_id={''}
            onPrint={setPrintFunction}
            editingProblemId={editingProblemId}
          />

          {isLoadingMore && (
            <div className="py-4 text-center text-sm text-gray-500">
              문제 불러오는 중...
            </div>
          )}

          {/* 확대/축소 컨트롤 - 스크롤에 따라 함께 이동하도록 수정 */}
          <div className="sticky bottom-4 left-1/2 mx-auto my-4 flex w-fit -translate-x-1/2 items-center gap-2 rounded-3xl bg-white p-1 shadow">
            <button
              className="flex size-8 items-center justify-center rounded-l text-gray-600 hover:bg-gray-100"
              onClick={() => handleScaleChange(scale - 0.05)}
            >
              -
            </button>

            <div className="flex items-center px-2">
              <input
                type="number"
                min="1"
                max="100"
                value={Math.round(scale * 100)}
                onChange={handleInputChange}
                className="w-12 border-0 text-center focus:ring-0"
              />
              <span className="ml-1">%</span>
            </div>

            <button
              className="flex size-8 items-center justify-center text-gray-600 hover:bg-gray-100"
              onClick={() => handleScaleChange(scale + 0.05)}
            >
              +
            </button>

            {/* PDF 다운로드 버튼 */}
            <div className="mx-1 h-6 w-px bg-gray-300" />
            <div className="relative">
              <button
                className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => setShowPdfInput(prev => !prev)}
                disabled={isGeneratingPdf || localProblems.length === 0}
                title="PDF 다운로드"
              >
                <FileDown className="size-4" />
                <span>{isGeneratingPdf ? 'PDF 생성 중...' : 'PDF'}</span>
              </button>
              {showPdfInput && (
                <div className="absolute bottom-full right-0 z-20 mb-1 flex items-center gap-1 rounded-xl bg-white p-2 shadow-lg ring-1 ring-gray-200">
                  <input
                    type="text"
                    value={pdfFilename}
                    onChange={(e) => setPdfFilename(e.target.value)}
                    placeholder="프리셋_미리보기"
                    className="w-48 rounded-lg border border-gray-300 px-2 py-1 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    onKeyDown={(e) => { if (e.key === 'Enter') { handleDownloadPdf(); setShowPdfInput(false); } }}
                    autoFocus
                  />
                  <span className="text-xs text-gray-400">.pdf</span>
                  <button
                    onClick={() => { handleDownloadPdf(); setShowPdfInput(false); }}
                    disabled={isGeneratingPdf}
                    className="whitespace-nowrap rounded-lg bg-primary px-3 py-1 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                  >
                    생성
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex h-full items-center justify-center">
          <div className="rounded bg-gray-100 p-4 text-center text-gray-500">
            <p>선택된 문제가 없습니다.</p>
            <p className="mt-2">문제를 선택하면 여기에 미리보기가 표시됩니다.</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default PreviewSection; 